import path from 'path'
import moment from 'moment'
import { useContext, useRef } from 'react'
import { AppContext, AppContextType } from '@/AppContext'
import ElectronIPC from '@/services/ElectronIPC'
import { sleep } from '@/utils'
import { RemoteRecordingErrorCodes, RemoteRecordingStatus } from '@/pages/CollaboratePage/Types'
import { ConversionFileInfo, useWebmConverter } from '@/services/useWebmConverter'
import useRootSnackbar from '@/services/useRootSnackbar'
import { env } from '@/services/EnvironmentVariables'
import { media_recorder_audio_bitrate } from '@/services/Constants'
import {
    AudioEngineRemoteAudio,
    P2pRemoteAudio,
    RoomContext,
    RoomContextState,
    RoomContextType,
    SfuRemoteAudio,
} from '@/RoomContext'
import useRoomContextRef from '@/services/useRoomContextRef'
import useUserContextRef from '@/services/useUserContextRef'
import { MusePeer } from '@/services/RoomContextMusePeer'
import useAppContextRef from '@/services/useAppContextRef'
import FakeMediaStreamTrack from '@/core/AudioEngine/FakeMediaStreamTrack'
import useAudioSettingsStoreActions from '@/core/useAudioSettingsStoreActions'

/*

recording remote peer (me recording them)
after start:
{
    "isBeingRecorded": false,
    "isRecording": true,
    "peerSessionId": "8255845b-b3a1-4904-9db8-ff6d78ae0eca",
    "sourceTrackId": "fcce81042267f1e1b1f87e8c3b9e3091d4b4cc7e24384a93ed46c2efc683fb6b"
}
after stop:
{
    "isBeingRecorded": false,
    "isRecording": false,
    "peerSessionId": "8255845b-b3a1-4904-9db8-ff6d78ae0eca",
    "sourceTrackId": "fcce81042267f1e1b1f87e8c3b9e3091d4b4cc7e24384a93ed46c2efc683fb6b"
}

being recorded by remote peer (them recording me)
after start:
{
    "isBeingRecorded": true,
    "recordInitiatorPeer": {
        "userUrn": "615ead840b53631bca4365e9",
        "username": "sungeunchoi.hj"
    },
    "recordingTrack": {
        "label": "CABLE Output (VB-Audio Virtual Cable)",
        "displayLabel": "CABLE Output (VB-Audio Virtual Cable)",
        "deviceId": "d765acb1ed0174ccb37db8a56bd45f7be623c03d835b4fb8c040df7830c2bc1d",
        "nativeDeviceId": null,
        "isSystemAudio": true,
        "isMuseAudioShare": true,
        "isMuseDAWBridge": true,
        "isHighQuality": true,
        "enableLocalLoopback": true,
        "bitrate": 44100
    }
}
after stop:
{
    "isBeingRecorded": false,
    "recordInitiatorPeer": null,
    "recordingTrack": null
}

*/
// recording conversion settings
const outputContainer = 'wav'
// const audioCodec = 'wav'
const conversionFlags = [
    // '-c:a', audioCodec,  // convert opus -> aac
    '-q:a',
    '0', // convert opus -> aac w/ max quality
]

// a bit nasty, but should work. recall that there can be multiple copies of useRemoteRecordingActions().
let recorder: MediaRecorder | null = null
let audioEngineLocalRecording: FakeMediaStreamTrack | null = null
let remoteRecordingSavefilePath: ConversionFileInfo | null = null

function useRemoteRecordingActions(
    sendMessageToPeer: (sessionId: string, obj: any, options?: { noStringify?: boolean }) => void,
    stopRemoteAudioRecord: (peer: MusePeer, deviceId?: string) => void,
    calcCurrRemoteRecordingPeerAudio: (
        roomState: RoomContextState
    ) => { device: P2pRemoteAudio | SfuRemoteAudio | AudioEngineRemoteAudio | null; peer: MusePeer } | null
) {
    const { dispatch: appDispatch } = useContext(AppContext) as AppContextType
    const { dispatch: roomDispatch } = useContext(RoomContext) as RoomContextType

    const { appStateRef } = useAppContextRef()
    const { userStateRef } = useUserContextRef()
    const { roomStateRef } = useRoomContextRef()

    const { getAudioOutputSettings } = useAudioSettingsStoreActions()

    const RootSnackbar = useRootSnackbar()

    const { convertWebm } = useWebmConverter({ pathDelimiter: env.OS === 'win32' ? '\\' : '/' })

    // const remoteRecordingStatusRef = useRef<RemoteRecordingStatus>({ isBeingRecorded: false })
    const remoteRecordingStatus = roomStateRef.current.remoteRecordingStatus

    // A wrapper to also set the ref version of the `remoteRecordingStatus` variable.
    const set_remoteRecordingStatus = (status: RemoteRecordingStatus) => {
        // remoteRecordingStatusRef.current = status
        roomDispatch({
            type: 'update_room_context',
            value: {
                remoteRecordingStatus: status,
            },
        })
    }

    const calculatePrerollDurationMs = () => {
        if (appStateRef.current.appSettings.previewBeatsEnabled) {
            const { tempo, timeSignatureTop } = appStateRef.current.appSettings
            const msWait = (60 / tempo) * timeSignatureTop * 1000
            return msWait
        } else {
            return 0
        }
    }

    const playPreviewToneWhileBlocking = async () => {
        // Actual recording starts after the preview beats.
        if (appStateRef.current.appSettings.previewBeatsEnabled) {
            const { tempo, timeSignatureTop } = appStateRef.current.appSettings
            // for (let i=0; i<timeSignatureTop; ++i) {
            //     //console.log(`[${moment().format('mm:ss.SSS')}] playTone()`)
            //     //playTone({ tone: 1000, durationSeconds: 0.075 })
            //     let msWait = (60 / tempo) * 1000
            //     await sleep(msWait)
            // }

            let msWait = (60 / tempo) * timeSignatureTop * 1000
            await sleep(msWait)
        }
    }

    // const recorder = useRef<MediaRecorder>()
    const forceStopMediaRecorder = () => {
        if (recorder?.state === 'recording') {
            recorder?.stop()
        }
        if (appStateRef.current.appSettings.remoteRecordingIsAdvancedMode) {
            ElectronIPC.sendDAWControlCommand({ command: 'stop-recording' })
        }
        if (audioEngineLocalRecording) {
            console.log('&&& audioEngineLocalRecording.stopLocalRecording()')
            audioEngineLocalRecording.stopLocalRecording()
            audioEngineLocalRecording = null

            if (remoteRecordingStatus.fileInfo) {
                onSuccess(remoteRecordingStatus.fileInfo)
            }
        }
        set_remoteRecordingStatus({
            isBeingRecorded: false,
            recordInitiatorPeer: null,
            recordingTrack: null,
        })
    }

    const onSuccess = (convertedFileInfo: ConversionFileInfo) => {
        RootSnackbar.show({
            severity: 'success',
            message: `Saved local recording "${convertedFileInfo.outputFileFullPath}".`,
        })
        // save `appStateRef.current.mostRecentRemoteRecordingInfo`, if changed.
        if (appStateRef.current.appState.mostRecentRemoteRecordingInfo?.path !== convertedFileInfo.outputFileFullPath) {
            appDispatch({
                type: 'update_app_state',
                value: {
                    mostRecentRemoteRecordingInfo: {
                        path: convertedFileInfo.outputFileFullPath,
                        filename: convertedFileInfo.outputFilename,
                        directory: convertedFileInfo.outputFileDirectory,
                    },
                },
            })
        }
    }

    /**
     * A request arrived from peer telling us to record audio track with id `sourceTrackId`.
     * We will be sending the recorded audio data chunks over remotely to the peer.
     *
     * Possible error codes:
     *   'recordee_remote_recording_disabled'
     *   'track_not_found_on_recordee'
     *   'peer_not_found_on_recordee'
     */
    const onStartRemoteRecordingHandler = async (senderSessionId: string, sourceTrackId: string): Promise<void> => {
        const {
            remoteRecordingIsAdvancedMode,
            remoteRecordingIoBufferSize,
            remoteRecordingSampleRate,
            remoteRecordSaveDirectory,
            remoteRecordingDisabled,
            delta_alpha,
        } = appStateRef.current.appSettings

        const myAudioTracks = userStateRef.current.mediaStreams.audioInputs

        // Check if we can remote-record this user.
        if (remoteRecordingDisabled) {
            console.log(
                `[${moment().format('mm:ss.SSS')}] onStartRemoteRecordingHandler: appSettings.remoteRecordingDisabled is set to true so returning error.`
            )
            sendMessageToPeer(senderSessionId, {
                type: 'remote-recording-error',
                data: {
                    error: true,
                    code: 'recordee_remote_recording_disabled',
                },
            })
            return
        }

        console.log(
            `[${moment().format('mm:ss.SSS')}] onStartRemoteRecordingHandler: sourceTrackId=${sourceTrackId}` +
                `My audio tracks: ${JSON.stringify(myAudioTracks.map((t) => t.id))}`
        )
        const atrack_rec = myAudioTracks.filter((track) => track.info.deviceId === sourceTrackId)[0]
        if (!atrack_rec) {
            console.error(`Could not find track with id=${sourceTrackId} to record in myAudioTracks list. No-op.`)
            // REFACTOR: show alert.
            sendMessageToPeer(senderSessionId, {
                type: 'remote-recording-error',
                data: {
                    error: true,
                    code: 'track_not_found_on_recordee',
                },
            })
            return
        }

        // generate and set the save-file path, to be used later if needed.
        const filenameBase = `muse-my-recording_${moment().format('MM-DD-HHmmss')}`
        const filename = filenameBase + '.webm'
        let localRecordingSavefilePath: any = null
        if (appStateRef.current.appSettings.recordeeSaveLocalRecording) {
            localRecordingSavefilePath = {
                outputFilename: filename,
                outputFilenameBase: filenameBase,
                outputFileDirectory: remoteRecordSaveDirectory,
                outputFileFullPath: path.join(remoteRecordSaveDirectory, filename),
            }
        }

        const peer = roomStateRef.current.peers[senderSessionId]
        if (peer == null) {
            sendMessageToPeer(senderSessionId, {
                type: 'remote-recording-error',
                data: {
                    error: true,
                    code: 'peer_not_found_on_recordee',
                },
            })
            return
        }

        if (atrack_rec.info.isAudioEngine) {
            // save local recording
            if (localRecordingSavefilePath && atrack_rec instanceof FakeMediaStreamTrack) {
                const filename = filenameBase + '.wav'
                const filePath = remoteRecordSaveDirectory + '/' + filename
                atrack_rec.startLocalRecording(filePath)
                audioEngineLocalRecording = atrack_rec // nasty

                set_remoteRecordingStatus({
                    isBeingRecorded: true,
                    recordInitiatorPeer: { userUrn: peer.userUrn, username: peer.username, sessionId: peer.sessionId },
                    recordingTrack: atrack_rec.info,
                    fileInfo: {
                        outputFilename: filename,
                        outputFilenameBase: filenameBase,
                        outputFileDirectory: remoteRecordSaveDirectory,
                        outputFileFullPath: filePath,
                    },
                })
            } else {
                set_remoteRecordingStatus({
                    isBeingRecorded: true,
                    recordInitiatorPeer: { userUrn: peer.userUrn, username: peer.username, sessionId: peer.sessionId },
                    recordingTrack: atrack_rec.info,
                })
            }

            // no-op for audio engine track.
            return
        }

        console.log(
            `[${moment().format('mm:ss.SSS')}] onStartRemoteRecordingHandler: sendDAWControlCommand start-recording`
        )
        if (remoteRecordingIsAdvancedMode) {
            ElectronIPC.sendDAWControlCommand({ command: 'start-recording' })
        }

        const options = {
            mimeType: 'audio/webm;codecs=opus',
            audioBitsPerSecond: media_recorder_audio_bitrate,
        }
        const stream = new MediaStream([atrack_rec])

        // await sleep(appContext.state.appSettings.delta_dawcommand) // delta_dawcommand
        if (remoteRecordingIsAdvancedMode) {
            console.log(
                `[${moment().format('mm:ss.SSS')}] onStartRemoteRecordingHandler: calling playPreviewToneWhileBlocking()`
            )
            await playPreviewToneWhileBlocking()

            // NOTE: Buffer Size (number of samples) / Sample Rate (kHz) = Expected Latency (ms).
            // NOTE: Multiply by 2 to get "roundtrip" latency
            const calculatedLatency = Math.ceil(
                (remoteRecordingIoBufferSize / remoteRecordingSampleRate) * 1000 * 2 + delta_alpha
            )
            console.log(
                `[${moment().format('mm:ss.SSS')}] onStartRemoteRecordingHandler: Exited playPreviewToneWhileBlocking(). ` +
                    `Sleeping calculatedLatency=${calculatedLatency} ms.`
            )
            await sleep(calculatedLatency)
            console.log(
                `[${moment().format('mm:ss.SSS')}] onStartRemoteRecordingHandler: Exited sleep(${calculatedLatency}).`
            )
        }

        let _isStopped = false
        let _chunkIndex = 0
        console.log(`[${moment().format('mm:ss.SSS')}] onStartRemoteRecordingHandler: creating MediaRecorder...`)
        recorder = new MediaRecorder(stream, options)
        console.log(`[${moment().format('mm:ss.SSS')}] onStartRemoteRecordingHandler: creating MediaRecorder done.`)
        recorder.ondataavailable = async (ev) => {
            const data = ev.data
            console.log(
                `[${moment().format('mm:ss.SSS')}] ondataavailable chunkIndex=${_chunkIndex} ` +
                    `data=<Blob size=${data.size.toLocaleString()}`
            )
            const b = await ev.data.arrayBuffer()
            // if (peer == null || peer.connection == null) {
            //     forceStopMediaRecorder()
            //     return
            // }

            try {
                sendMessageToPeer(senderSessionId, b, { noStringify: true })
                // peer.connection.send(b)
            } catch (e) {
                forceStopMediaRecorder()
            }

            const chunk = new Uint8Array(b)
            if (appStateRef.current.appSettings.recordeeSaveLocalRecording) {
                console.log(
                    `[${moment().format('mm:ss.SSS')}] Saving chunk ${_chunkIndex} to file ${localRecordingSavefilePath.outputFileFullPath}`
                )
                ElectronIPC.saveAudioFileChunk({
                    filePath: localRecordingSavefilePath.outputFileFullPath,
                    uint8Array: chunk,
                })
            }

            if (_isStopped) {
                try {
                    sendMessageToPeer(
                        senderSessionId,
                        new Uint8Array([...'muse-rec-stop'].map((c) => c.charCodeAt(0))),
                        { noStringify: true }
                    )
                } catch (e) {
                    forceStopMediaRecorder()
                }

                // convert webm to aac when recording done.
                if (localRecordingSavefilePath) {
                    convertWebm(
                        localRecordingSavefilePath,
                        outputContainer,
                        conversionFlags,
                        // success
                        onSuccess,
                        // error
                        (code: number, message?: string) => {
                            RootSnackbar.show({
                                severity: 'error',
                                message: `Failed to convert to ${outputContainer}. Code: ${code} Message: "${message}".`,
                            })
                        }
                    )
                }
            }

            _chunkIndex += 1
        }
        recorder.onerror = (e) => {
            console.error('recorder error: %O', e)
            // TODO: show alert.
        }
        recorder.onstop = () => {
            console.log(`[${moment().format('mm:ss.SSS')}] recorder stop.`)
            _isStopped = true
        }

        // Start recording `atrack_rec`.
        console.log(`[${moment().format('mm:ss.SSS')}] onStartRemoteRecordingHandler: start recording.`)
        recorder.start(250) // arg: timeslice

        set_remoteRecordingStatus({
            isBeingRecorded: true,
            recordInitiatorPeer: { userUrn: peer.userUrn, username: peer.username, sessionId: peer.sessionId },
            recordingTrack: atrack_rec.info,
        })
    }

    /**
     * Stop recording as a recordee.
     * Called when the stop-remote-recording signal received from the recorder.
     */
    const onStopRemoteRecordingHandler = () => {
        if (recorder && recorder.state === 'recording') {
            if (appStateRef.current.appSettings.remoteRecordingIsAdvancedMode) {
                ElectronIPC.sendDAWControlCommand({ command: 'stop-recording' })
            }
            recorder && recorder.stop()
        }

        if (audioEngineLocalRecording) {
            audioEngineLocalRecording.stopLocalRecording()
            audioEngineLocalRecording = null

            if (roomStateRef.current.remoteRecordingStatus.fileInfo) {
                onSuccess(roomStateRef.current.remoteRecordingStatus.fileInfo)
            }
        }

        set_remoteRecordingStatus({
            isBeingRecorded: false,
            recordInitiatorPeer: null,
            recordingTrack: null,
        })
    }

    const onRemoteRecordingErrorHandler = (data: any) => {
        set_remoteRecordingStatus({
            ...remoteRecordingStatus,
            isBeingRecorded: false,
            error: {
                code: data?.code || 'unknown',
            },
        })
    }

    /**
     * Corresponds to the trigger on/off of the record (R) button.
     * Sends start-remote-recording signal to the peer and receives audio chunks from the peer
     * (through onRemoteRecordingChunkHandler).
     *
     * souceTrackLabel needed for op=='start'.
     */
    const onRecordingTriggered = async (
        peerSessionId: string,
        sourceTrackId: string,
        op: 'start' | 'stop',
        sourceTrackLabel?: string
    ) => {
        if (op === 'start') {
            console.log(`useRemoteRecording: at startRemoteRecording. peerSessionId=${peerSessionId}`)
            const peer = roomStateRef.current.peers[peerSessionId]
            if (peer == null) {
                console.error(
                    `useRemoteRecording: at startRemoteRecording: could not find peer with ` +
                        `peerSessionId=${peerSessionId} in the peerTable. No-op.`
                )
                return
            }

            const stream = peer.remoteAudios.find((a) => a.info.deviceId === sourceTrackId)
            const isAudioEngine = Boolean(stream?.info?.isAudioEngine)

            // set status
            set_remoteRecordingStatus({
                ...remoteRecordingStatus,
                isRecording: true,
                peerSessionId: peerSessionId,
                sourceTrackId: sourceTrackId,
                error: undefined,
            })

            // send message to peer with sessionId `peerSessionId` to start recording.
            console.log(`useRemoteRecording: at startRemoteRecording: sending message type="start-remote-recording"`)
            sendMessageToPeer(peerSessionId, {
                type: 'start-remote-recording',
                data: {
                    sourceTrackId: sourceTrackId,
                    isAdvanced: appStateRef.current.appSettings.remoteRecordingIsAdvancedMode,
                },
            })

            // send messasge to MuseReceivePlugin.
            ElectronIPC.sendToDawPlugin('MuseReceivePlugin', {
                type: 'museAppStartedRecording',
                deviceTrackId: sourceTrackId,
                deviceName: sourceTrackLabel,
                deviceUsername: peer.username,
            })

            // generate and set the save-file path, to be used later if needed.
            const filenameBase = `muse-audio-recording_${moment().format('MM-DD-HHmmss')}`
            const ext = isAudioEngine ? '.wav' : '.webm'
            const filename = filenameBase + ext
            if (appStateRef.current.appSettings.recorderSaveRemoteRecording) {
                remoteRecordingSavefilePath = {
                    outputFilename: filename,
                    outputFilenameBase: filenameBase,
                    outputFileDirectory: appStateRef.current.appSettings.remoteRecordSaveDirectory,
                    outputFileFullPath: path.join(appStateRef.current.appSettings.remoteRecordSaveDirectory, filename),
                }
            }

            if (isAudioEngine && stream?.track instanceof FakeMediaStreamTrack) {
                let filePath = null
                if (appStateRef.current.appSettings.recorderSaveRemoteRecording) {
                    const filename = filenameBase + '.wav'
                    filePath = path.join(appStateRef.current.appSettings.remoteRecordSaveDirectory, filename)
                }

                let museRecordingDeviceId = userStateRef.current.mediaSelection.audioOutputMuseRecording
                let source =
                    userStateRef.current.mediaSources.audioOutputsAll.find(
                        (a) => a.deviceId === museRecordingDeviceId
                    ) || null
                let settings = getAudioOutputSettings(museRecordingDeviceId)
                stream.track.startRemoteRecording(filePath, source, settings)

                set_remoteRecordingStatus({
                    ...remoteRecordingStatus,
                    isRecording: true,
                    peerSessionId: peerSessionId,
                    sourceTrackId: sourceTrackId,
                    error: undefined,
                    ...(filePath
                        ? {
                              fileInfo: {
                                  outputFilename: filename,
                                  outputFilenameBase: filenameBase,
                                  outputFileDirectory: appStateRef.current.appSettings.remoteRecordSaveDirectory,
                                  outputFileFullPath: filePath || '',
                              },
                          }
                        : {}),
                })
            }

            // Only if remote recording is in advanced mode:
            if (!isAudioEngine && appStateRef.current.appSettings.remoteRecordingIsAdvancedMode) {
                // Tell the RemoteRecordingPlayer module in the main thread to start listening.
                // Audio data packets will be coming in through the `onRemoteRecordingChunkHandler()`
                // callback above.
                ElectronIPC.remoteRecordingPlayer({
                    opcode: 'start',
                    data: {
                        prerollDuration: calculatePrerollDurationMs(),
                        forwardCompensationMs: appStateRef.current.appSettings.remoteRecordingForwardCompensation,
                    },
                })

                // Handle events from the RemoteRecordingPlayer module:
                ElectronIPC.setRemoteRecordingPlayerEventHandler((event, data) => {
                    console.log(`remote-recording returned event ` + JSON.stringify(data))
                    const eventName = data?.eventName
                    if (eventName === 'end') {
                        stopRemoteAudioRecord(peer)
                    } else if (eventName === 'stream_timeout') {
                        // The stream timed out.
                        stopRemoteAudioRecord(peer)

                        // Set error, only if a previous error is not set.
                        if (!roomStateRef.current.remoteRecordingStatus.error) {
                            set_remoteRecordingStatus({
                                ...remoteRecordingStatus,
                                peerSessionId: peerSessionId,
                                sourceTrackId: sourceTrackId,
                                error: { code: 'stream_timeout' },
                            })
                        }

                        ElectronIPC.remoteRecordingPlayer({ opcode: 'end', data: {} })
                        // session.triggerStopPeerRemoteRecording(peerSessionId)
                        sendMessageToPeer(peerSessionId, {
                            type: 'stop-remote-recording',
                            data: {},
                        })
                    }
                })
            }
        } else {
            sendMessageToPeer(peerSessionId, {
                type: 'stop-remote-recording',
                data: {},
            })

            // send messasge to MuseReceivePlugin.
            ElectronIPC.sendToDawPlugin('MuseReceivePlugin', {
                type: 'museAppStoppedRecording',
                deviceTrackId: sourceTrackId,
            })

            set_remoteRecordingStatus({
                ...remoteRecordingStatus,
                isRecording: false,
            })

            const peer = roomStateRef.current.peers[peerSessionId]
            if (peer == null) {
                console.error(
                    `useRemoteRecording: at stopRemoteRecording: could not find peer with ` +
                        `peerSessionId=${peerSessionId} in the peerTable. No-op.`
                )
                return
            }

            const stream = peer.remoteAudios.find((a) => a.info.deviceId === sourceTrackId)
            const isAudioEngine = Boolean(stream?.info?.isAudioEngine)

            if (isAudioEngine && stream?.track instanceof FakeMediaStreamTrack) {
                stream.track.stopRemoteRecording()
                if (remoteRecordingStatus.fileInfo) {
                    onSuccess(remoteRecordingStatus.fileInfo)
                }
            }
        }
    }

    const remoteRecordingChunkStopHandler = () => {
        if (!remoteRecordingSavefilePath) {
            return
        }

        convertWebm(
            remoteRecordingSavefilePath,
            outputContainer,
            conversionFlags,
            // success
            (convertedFileInfo) => {
                RootSnackbar.show({
                    severity: 'success',
                    message: `Audio recording saved`,
                })
                // save `appStateRef.current.mostRecentRemoteRecordingInfo`, if changed.
                if (
                    appStateRef.current.appState.mostRecentRemoteRecordingInfo?.path !==
                    convertedFileInfo.outputFileFullPath
                ) {
                    appDispatch({
                        type: 'update_app_state',
                        value: {
                            mostRecentRemoteRecordingInfo: {
                                path: convertedFileInfo.outputFileFullPath,
                                filename: convertedFileInfo.outputFilename,
                                directory: convertedFileInfo.outputFileDirectory,
                            },
                        },
                    })
                }
            },
            // error
            (code: number, message?: string) => {
                RootSnackbar.show({
                    severity: 'error',
                    message: `Failed to convert to aac. Code: ${code} Message: "${message}".`,
                })
            }
        )
    }

    const remoteRecordingchunkTimeoutTimerRef = useRef<NodeJS.Timeout | undefined>(undefined)
    const onRemoteRecordingChunkHandler = async ({
        senderSessionId,
        isFirstChunk,
        chunk,
        isStop,
        errorCode,
    }: {
        senderSessionId: string
        isFirstChunk: boolean
        chunk: Uint8Array
        isStop: boolean
        errorCode?: RemoteRecordingErrorCodes
    }) => {
        if (errorCode) {
            set_remoteRecordingStatus({
                ...remoteRecordingStatus,
                isRecording: false,
                error: { code: errorCode },
            })
            return
        }

        clearTimeout(remoteRecordingchunkTimeoutTimerRef.current)
        remoteRecordingchunkTimeoutTimerRef.current = setTimeout(() => {
            // runs if there is a sufficient gap in time between onRemoteRecordingChunkHandler
            // until `isStop`.
            set_remoteRecordingStatus({
                ...remoteRecordingStatus,
                isRecording: false,
                error: { code: 'stream_timeout' },
            })

            if (remoteRecordingSavefilePath && appStateRef.current.appSettings.recorderSaveRemoteRecording) {
                remoteRecordingChunkStopHandler()
            }
        }, 5000)
        if (isStop) {
            clearTimeout(remoteRecordingchunkTimeoutTimerRef.current)
        }

        // safety check: check if there's a recording currently running. If not, then tell the peer to stop.
        let currRecording = calcCurrRemoteRecordingPeerAudio(roomStateRef.current)
        if (!currRecording) {
            console.log(`There is not a recording going on but peer is sending recording chunks. Telling them to stop.`)
            // session.triggerStopPeerRemoteRecording(peerSessionId)
            sendMessageToPeer(senderSessionId, {
                type: 'stop-remote-recording',
                data: {},
            })
            return
        }

        // Save audio file to disk if requested by the user.
        if (!isStop && chunk && remoteRecordingSavefilePath) {
            if (appStateRef.current.appSettings.recorderSaveRemoteRecording) {
                // write chunk to file
                ElectronIPC.saveAudioFileChunk({
                    filePath: remoteRecordingSavefilePath.outputFileFullPath,
                    uint8Array: chunk,
                })
            }
        }
        if (isStop && remoteRecordingSavefilePath && appStateRef.current.appSettings.recorderSaveRemoteRecording) {
            remoteRecordingChunkStopHandler()
        }

        // Feed the data into the RemoteRecordingPlayer stream.
        if (appStateRef.current.appSettings.remoteRecordingIsAdvancedMode) {
            if (isStop) {
                ElectronIPC.remoteRecordingPlayer({ opcode: 'end', data: {} })
            } else {
                ElectronIPC.remoteRecordingPlayer({ opcode: 'data', data: { chunk: chunk } })
            }
        }
    }

    return {
        remoteRecordingStatus,
        onStartRemoteRecordingHandler,
        onStopRemoteRecordingHandler,
        onRemoteRecordingErrorHandler,
        forceStopMediaRecorder,
        onRemoteRecordingChunkHandler,
        onRecordingTriggered,
    }
}

export default useRemoteRecordingActions
